import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { tokens } from '../../global/theme/tokens';
import { AI_ENTITY_TYPES, lookupReferenceLabel } from './aiChatEntities';

export const referenceLabel = (reference, t) =>
  reference.label ||
  lookupReferenceLabel(reference) ||
  `${t(AI_ENTITY_TYPES[reference.type]?.label || 'Reference')} #${reference.id}`;

/**
 * variant: 'active' (will be sent), 'sent' (already part of the chat),
 * 'ghost' (dismissed page context, click to add again), 'link' (transcript)
 */
export const AiReferenceChip = ({
  reference,
  variant = 'active',
  onRemove,
  onClick,
  tooltip,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const meta = AI_ENTITY_TYPES[reference.type];
  const Icon = meta?.icon;
  const color = colors.colorPool[meta?.colorIndex ?? 8];
  const isGhost = variant === 'ghost';

  const chip = (
    <Box
      onClick={onClick}
      className="flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-lg min-w-0 max-w-full"
      sx={{
        backgroundColor: isGhost ? 'transparent' : `${color}20`,
        border: `1px ${isGhost ? 'dashed' : 'solid'} ${color}${isGhost ? '80' : '40'}`,
        opacity: variant === 'sent' ? 0.75 : 1,
        cursor: onClick || variant === 'link' ? 'pointer' : 'default',
        transition: '0.2s ease',
        '&:hover':
          onClick || variant === 'link'
            ? { backgroundColor: `${color}30` }
            : undefined,
      }}
    >
      {isGhost ? (
        <AddIcon sx={{ fontSize: 14, color }} />
      ) : (
        Icon && <Icon sx={{ fontSize: 14, color }} />
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{ color: colors.text, fontWeight: 500, minWidth: 0 }}
      >
        {referenceLabel(reference, t)}
      </Typography>
      {variant === 'sent' && <CheckIcon sx={{ fontSize: 14, color }} />}
      {onRemove && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{ p: 0.25, color: colors.text, opacity: 0.6 }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );

  const content =
    variant === 'link' && meta ? (
      <Link to={meta.route(reference.id)} className="min-w-0 max-w-full">
        {chip}
      </Link>
    ) : (
      chip
    );

  return tooltip ? (
    <Tooltip title={tooltip} disableInteractive>
      {content}
    </Tooltip>
  ) : (
    content
  );
};
